import { Link, useNavigate } from "react-router-dom";
import {
  CompanyProfileTab,
  MainRoutes,
  MemberProfileTab,
  ProjectProfileTab,
} from "@router/route-params";
import { ReactChildrenOnly } from "@utils/utility-types";
import { useGenerateRoutes } from "@router/router-helper";
import { usePreservedSearch } from "@hooks/use-preserved-search";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { LocationProp } from "@utils/track-event/event-types";
import {
  BaseCompanyIdProps,
  BaseMemberIdProps,
  BaseProjectIdProps,
  SubjectType,
} from "@custom-types/base-types";
import { Box } from "@mui-wrapper";
import LaunchIcon from "@mui/icons-material/Launch";
import { MainPageEvents } from "@utils/track-event/track-event-list";
import { IconButton, Tooltip, grey } from "@flat-ui";
import { formatSubjectType } from "@utils/data-display";
import { TextWithTooltip } from "@components/common/text-with-tooltip";

/** Provide a link to the homepage from anywhere */
export function HomeLink({ children }: ReactChildrenOnly): React.JSX.Element {
  const { preservedSearch } = usePreservedSearch();
  return <Link to={preservedSearch(MainRoutes.root)}>{children}</Link>;
}

type ProjectLinkProps = BaseProjectIdProps &
  LocationProp & {
    type: Extract<SubjectType, "PROJECT">;
    projectProfileTab?: ProjectProfileTab;
  };

type MemberLinkProps = BaseMemberIdProps &
  LocationProp & {
    type: Extract<SubjectType, "USER">;
    memberProfileTab?: MemberProfileTab;
  };

type WorkspaceLinkProps = BaseCompanyIdProps &
  LocationProp & {
    type: Extract<SubjectType, "COMPANY">;
    companyProfileTab?: CompanyProfileTab;
  };

type InternalLinkProps =
  ProjectLinkProps | MemberLinkProps | WorkspaceLinkProps;

/** Generates the URL string for the given link props */
function useInternalLinkUrl(props: InternalLinkProps): string {
  const { generateProjectRoute, generateMemberRoute, generateCompanyRoute } =
    useGenerateRoutes();

  switch (props.type) {
    case "PROJECT": {
      const { pathname, search } = generateProjectRoute({
        projectId: props.projectId,
        projectProfileTab: props.projectProfileTab,
      });
      return `${pathname}${search}`;
    }
    case "USER": {
      const { pathname, search } = generateMemberRoute({
        memberId: props.memberId,
        memberProfileTab: props.memberProfileTab,
      });
      return `${pathname}${search}`;
    }
    case "COMPANY": {
      const { pathname, search } = generateCompanyRoute({
        companyId: props.companyId,
        companyProfileTab: props.companyProfileTab,
      });
      return `${pathname}${search}`;
    }
  }
}

// --- Components ---

type InternalLinkButtonProps = InternalLinkProps & {
  /** Whether to open the profile in a new tab. Defaults to false */
  shouldOpenInNewTab?: boolean;
};

/** Provide a launch icon button that opens a profile page */
export function InternalLinkButton(
  props: InternalLinkButtonProps
): React.JSX.Element {
  const url = useInternalLinkUrl(props);
  const { trackEvent } = useTrackEvent();
  const navigate = useNavigate();

  function handleClick(event: React.MouseEvent): void {
    event.stopPropagation();
    trackEvent({
      name: MainPageEvents.openItem,
      props: {
        target: props.type,
        location: props.location,
      },
    });

    if (props.shouldOpenInNewTab) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      navigate(url);
    }
  }

  return (
    <Tooltip title={`Open ${formatSubjectType(props.type)} profile`}>
      <IconButton onClick={handleClick} size="s" sx={{ color: grey[1300] }}>
        <LaunchIcon />
      </IconButton>
    </Tooltip>
  );
}

type InternalLinkTextProps = InternalLinkProps & {
  /** The text to be shown for link */
  text: string;
};

/** Provide a text link that opens a profile page in the same tab */
export function InternalLinkText(
  props: InternalLinkTextProps
): React.JSX.Element {
  const url = useInternalLinkUrl(props);
  const { trackEvent } = useTrackEvent();

  return (
    <Box
      component={Link}
      to={url}
      sx={{ minWidth: 0 }}
      onClick={() =>
        trackEvent({
          name: MainPageEvents.openItem,
          props: {
            location: props.location,
            target: props.type,
          },
        })
      }
    >
      <TextWithTooltip text={props.text} />
    </Box>
  );
}
